import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { X } from 'lucide-react';
import { load as loadSuggestedRoles } from 'redux/modules/suggestedRoles2';
import SelectSelectize from 'components/Shared/Selectors/SelectSelectize';
import styles from './ProjectRolesField.module.scss';

const MAX_ROLES = 5;

/**
 * Project roles as wrap-friendly chips + dashed "+ Add role" picker.
 * Shared by create and edit project forms.
 */
@connect(
  (state) => ({
    loadedSuggestedRoles: state.suggestedRoles2.loaded,
    loadingSuggestedRoles: state.suggestedRoles2.loading,
    suggestedRoles: state.suggestedRoles2.suggestedRoles,
    roleEntities: state.entities.roles,
  }),
  {
    loadSuggestedRoles,
  }
)
export default class ProjectRolesField extends Component {
  static propTypes = {
    existing: PropTypes.arrayOf(PropTypes.string),
    onSelect: PropTypes.func,
    disabled: PropTypes.bool,
    maxValues: PropTypes.number,
    error: PropTypes.string,
    loadedSuggestedRoles: PropTypes.bool,
    loadingSuggestedRoles: PropTypes.bool,
    suggestedRoles: PropTypes.array,
    roleEntities: PropTypes.object,
    loadSuggestedRoles: PropTypes.func,
  };

  static defaultProps = {
    existing: [],
    maxValues: MAX_ROLES,
    disabled: false,
    error: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      selectedRoles: this.mapExisting(props.existing),
      showPicker: false,
      options: this.buildOptions(props),
    };
  }

  componentDidMount() {
    if (this.state.selectedRoles.length && this.props.onSelect) {
      this.props.onSelect(this.state.selectedRoles);
    }

    if (!this.props.loadingSuggestedRoles && !this.props.loadedSuggestedRoles) {
      this.props.loadSuggestedRoles();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.selectedRoles !== this.state.selectedRoles &&
      this.props.onSelect
    ) {
      this.props.onSelect(this.state.selectedRoles);
    }

    if (
      prevProps.suggestedRoles.length !== this.props.suggestedRoles.length ||
      prevProps.roleEntities !== this.props.roleEntities
    ) {
      this.setState({ options: this.buildOptions() });
    }
  }

  mapExisting = (existing) =>
    (Array.isArray(existing) ? existing : []).map((role) => ({
      label: role,
      value: role,
    }));

  buildOptions = (props = this.props) =>
    props.suggestedRoles
      .map((roleId) => props.roleEntities[roleId])
      .filter(Boolean)
      .map(({ name }) => ({
        label: name,
        value: name,
      }))
      .sort((a, b) =>
        a.label.toUpperCase().localeCompare(b.label.toUpperCase())
      );

  handleRemove = (value) => {
    if (this.props.disabled) return;

    this.setState((state) => ({
      selectedRoles: state.selectedRoles.filter((role) => role.value !== value),
    }));
  };

  handleAdd = (selected) => {
    if (!selected || this.props.disabled) return;

    const { maxValues } = this.props;

    this.setState((state) => {
      if (state.selectedRoles.some((role) => role.value === selected.value)) {
        return { showPicker: false };
      }

      if (state.selectedRoles.length >= maxValues) {
        return { showPicker: false };
      }

      return {
        selectedRoles: [
          ...state.selectedRoles,
          { label: selected.value, value: selected.value },
        ],
        showPicker: false,
      };
    });
  };

  openPicker = () => {
    if (this.props.disabled) return;
    if (this.state.selectedRoles.length >= this.props.maxValues) return;
    this.setState({ showPicker: true });
  };

  render() {
    const { disabled, error, loadingSuggestedRoles, maxValues } = this.props;
    const { selectedRoles, showPicker, options } = this.state;

    const selectedValues = new Set(selectedRoles.map((role) => role.value));
    const availableOptions = options.filter(
      (option) => !selectedValues.has(option.value)
    );
    const canAdd = selectedRoles.length < maxValues && !disabled;

    return (
      <div>
        <span className={styles.label}>Project roles</span>
        <p className={styles.help}>
          Taken from your profile — remove any that don&apos;t apply here.
        </p>
        <div className={styles.chips}>
          {selectedRoles.map((role) => (
            <span key={role.value} className={styles.chip}>
              <span className={styles.chipLabel}>
                {role.label || role.value}
              </span>
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => this.handleRemove(role.value)}
                disabled={disabled}
                aria-label={`Remove ${role.label || role.value}`}
              >
                <X size={12} strokeWidth={2.5} />
              </button>
            </span>
          ))}
          {canAdd && (
            <button
              type="button"
              className={styles.addChip}
              onClick={this.openPicker}
            >
              + Add role
            </button>
          )}
        </div>
        {showPicker && canAdd && (
          <div className={styles.picker}>
            <SelectSelectize
              id="project-roles-add"
              hideResetButton
              options={availableOptions}
              values={null}
              onValuesChange={this.handleAdd}
              placeholder="Start typing a role"
              renderNoResultsFound={() => (
                <div className="no-results-found">
                  {loadingSuggestedRoles ? 'Loading...' : 'No results found'}
                </div>
              )}
            />
          </div>
        )}
        {error && <div className={styles.error}>{error}</div>}
      </div>
    );
  }
}
